import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';
import { TRIGGER_OBJECT } from '../../constants/ClickstreamConstants';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';

const EVENT_IDS = {
  INCOME_RENT_RATIO_COLLECTION: '6608',
  INCOME_RENT_NEGOTIABLE_CHECKBOX: '6609',
  MINIMUM_CREDIT_SCORE_CRITERIA: '6610',
  CREDIT_SCORE_NEGOTIABLE_CHECKBOX: '6611',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6608
const IncomeRentRatioCollection = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.INCOME_RENT_RATIO_COLLECTION,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',

      trigger_object_nm: triggerObject,
      trigger_source_nm: 'income_rent_ratio_field',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6609
const IncomeRentNegotiableCheckbox = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.INCOME_RENT_NEGOTIABLE_CHECKBOX,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'income_rent_ratio_negotiable',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6610
const MinimumCreditScoreCriteria = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.MINIMUM_CREDIT_SCORE_CRITERIA,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'min_credit_score_select',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6611
const CreditScoreNegotiableCheckbox = ({
  currentProperty,
  user,
  triggerLocation,
  triggerObject = TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  triggerObject?: (typeof TRIGGER_OBJECT)[keyof typeof TRIGGER_OBJECT];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CREDIT_SCORE_NEGOTIABLE_CHECKBOX,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'credit_score_negotiable_checkbox',
    },
    semantic: {
      semantic_event_nm: 'set_listing_criteria',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const FinancialExpectationsCSEvents = {
  CreditScoreNegotiableCheckbox,
  IncomeRentNegotiableCheckbox,
  IncomeRentRatioCollection,
  MinimumCreditScoreCriteria,
};

// Clickstream visualizer data-eventid attributes
CreditScoreNegotiableCheckbox.data_event_id = EVENT_IDS.CREDIT_SCORE_NEGOTIABLE_CHECKBOX;
IncomeRentNegotiableCheckbox.data_event_id = EVENT_IDS.INCOME_RENT_NEGOTIABLE_CHECKBOX;
IncomeRentRatioCollection.data_event_id = EVENT_IDS.INCOME_RENT_RATIO_COLLECTION;
MinimumCreditScoreCriteria.data_event_id = EVENT_IDS.MINIMUM_CREDIT_SCORE_CRITERIA;

export default FinancialExpectationsCSEvents;
